import React, { ChangeEvent, useState } from "react";
import Modal, { Styles } from "react-modal";
import ISerialWorkflowStep from "../../../../models/ISerialWorkflowStep";
import OwButton from "../../../shared/OwButton/OwButton";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import { OwFnInput } from "../../../shared/OwFnInput/OwFnInput";
import OwToggle from "../../../shared/OwToggle/OwToggle";

type Props = {
    isOpen: boolean;
    step: ISerialWorkflowStep | null;
    existingSteps: ISerialWorkflowStep[];
    onClose: () => void;
    onSave: (data: { name: string; is_production_start: boolean; is_terminal: boolean }) => void;
};

const modalStyle: Styles = {
    content: {
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        marginRight: "-50%",
        transform: "translate(-50%, -50%)",
        maxWidth: "30rem",
        width: "100%",
        color: "var(--main-text-color)",
        backgroundColor: "var(--main-bg-color)",
        border: "1px solid var(--minor-bg-color)",
    },
    overlay: {
        zIndex: 100,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
};

const SerialWorkflowStepModal: React.FC<Props> = ({ isOpen, step, existingSteps, onClose, onSave }) => {
    const [name, setName] = useState(step?.name || "");
    const [isProductionStart, setIsProductionStart] = useState(step?.is_production_start || false);
    const [isTerminal, setIsTerminal] = useState(step?.is_terminal || false);

    const otherStepHasProductionStart = existingSteps.some((s) => s.is_production_start && s.id !== step?.id);
    const otherStepHasTerminal = existingSteps.some((s) => s.is_terminal && s.id !== step?.id);

    const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
    };

    const handleProductionStartToggle = () => {
        setIsProductionStart(!isProductionStart);
    };

    const handleTerminalToggle = () => {
        setIsTerminal(!isTerminal);
    };

    const handleSave = () => {
        if (!name.trim()) return;
        onSave({
            name: name.trim(),
            is_production_start: isProductionStart,
            is_terminal: isTerminal,
        });
    };

    return (
        <Modal isOpen={isOpen} onRequestClose={onClose} style={modalStyle} ariaHideApp={false}>
            <div className="SerialWorkflowStepModal">
                <div className="d-flex justify-content-between mb-3">
                    <OwTitle leftIconName={"fad fa-industry"} title={step ? "edit_production_step" : "add_production_step"} tag={"h3"} />
                    <OwButton className={"btn-danger"} centerIconClass={"fas fa-times"} fn={onClose} ariaLabel="close" />
                </div>

                <div className="mb-3">
                    <label className="form-label fw-bold">Step Name</label>
                    <OwFnInput fn={handleNameChange} name={"name"} value={name} placeholder={"production_step_name"} skipTranslate={true} />
                </div>

                <div className="mb-3">
                    <OwToggle
                        fn={handleProductionStartToggle}
                        name={"is_production_start"}
                        label={"is_production_start"}
                        active={isProductionStart}
                        disabled={otherStepHasProductionStart && !isProductionStart}
                    />
                    {otherStepHasProductionStart && !isProductionStart && (
                        <small className="text-muted d-block mt-1">
                            <i className="fad fa-info-circle me-1" />
                            Another step is already the production start. Remove it there first.
                        </small>
                    )}
                </div>

                <div className="mb-3">
                    <OwToggle
                        fn={handleTerminalToggle}
                        name={"is_terminal"}
                        label={"is_terminal"}
                        active={isTerminal}
                        disabled={otherStepHasTerminal && !isTerminal}
                    />
                    {otherStepHasTerminal && !isTerminal && (
                        <small className="text-muted d-block mt-1">
                            <i className="fad fa-info-circle me-1" />
                            Another step is already the terminal step. Remove it there first.
                        </small>
                    )}
                </div>

                <div className="d-flex justify-content-end">
                    <OwButton
                        title={"save"}
                        className={"btn-primary"}
                        leftIconClass={"fas fa-save"}
                        fn={handleSave}
                        disabled={!name.trim()}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default SerialWorkflowStepModal;
